import { For, Show } from 'solid-js';

export interface HudTabItem {
  id: string;
  label: string;
  /** Right-hand detail, e.g. "· 84%". Optional. */
  detail?: string;
  /** true → live dot, false → idle dot, undefined → no dot. */
  live?: boolean;
}

/** The HUD switcher strip: model tabs in the hero, GPU tabs above the reactor. */
export function HudTabs(props: {
  items: HudTabItem[];
  selected: string;
  onSelect: (id: string) => void;
  label: string;
}) {
  let tabListRef: HTMLDivElement | undefined;

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!tabListRef) return;

    const currentIndex = props.items.findIndex(item => item.id === props.selected);
    let nextIndex: number | null = null;

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      nextIndex = (currentIndex + 1) % props.items.length;
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      nextIndex = (currentIndex - 1 + props.items.length) % props.items.length;
    } else if (e.key === 'Home') {
      e.preventDefault();
      nextIndex = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      nextIndex = props.items.length - 1;
    }

    if (nextIndex !== null) {
      const nextId = props.items[nextIndex]!.id;
      props.onSelect(nextId);
      // Move DOM focus to the newly selected tab, resolved by index not by state.
      const tabs = tabListRef.querySelectorAll('[role="tab"]');
      (tabs[nextIndex] as HTMLButtonElement)?.focus();
    }
  };

  return (
    <div ref={tabListRef} role="tablist" aria-label={props.label} class="mb-3.5 flex flex-wrap gap-2">
      <For each={props.items}>
        {(item) => {
          const on = () => item.id === props.selected;
          return (
            <button
              type="button"
              role="tab"
              aria-selected={on() ? 'true' : 'false'}
              tabindex={on() ? '0' : '-1'}
              onClick={() => {
                if (item.id !== props.selected) {
                  props.onSelect(item.id);
                }
              }}
              onKeyDown={handleKeyDown}
              class={`hud-clip hud-mono inline-flex min-h-[32px] items-center gap-1.5 border px-2.5 py-1 text-[0.625rem] uppercase tracking-[0.14em] ${
                on() ? 'border-line-strong bg-accent-soft text-holo' : 'border-line text-muted hover:text-ink'
              }`}
            >
              <Show when={item.live !== undefined}>
                <span
                  data-dot={item.live ? 'live' : 'idle'}
                  class={`h-1.5 w-1.5 flex-none rounded-full ${item.live ? 'bg-good' : 'bg-faint'}`}
                />
              </Show>
              {item.label}
              <Show when={item.detail}>
                {(d) => <small class="tracking-normal text-faint">{d()}</small>}
              </Show>
            </button>
          );
        }}
      </For>
    </div>
  );
}
